import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePlaybookBySlug, usePlaybookNodes, PlaybookNode } from "@/hooks/usePlaybooks";
import { getIcon } from "@/lib/iconMap";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import analytics from "@/lib/segment";
import { 
  ArrowRight, 
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  FileText,
  Loader2,
  RotateCcw,
  Anchor
} from "lucide-react";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";

export default function PublicPlaybook() {
  const { slug } = useParams();
  const [currentNodeId, setCurrentNodeId] = useState("start");
  const [history, setHistory] = useState<string[]>([]);
  const hasTrackedView = useRef(false);
  const hasTrackedCompletion = useRef(false);

  const { data: playbook, isLoading: playbookLoading } = usePlaybookBySlug(slug);
  const { data: nodes, isLoading: nodesLoading } = usePlaybookNodes(playbook?.id);

  // Track shared playbook view once playbook loads
  useEffect(() => {
    if (playbook && !hasTrackedView.current) {
      hasTrackedView.current = true;
      analytics.track("Shared Playbook Viewed", {
        playbook_name: playbook.title,
        playbook_slug: slug,
        playbook_category: playbook.category,
      });
    }
  }, [playbook, slug]);

  // Reset when playbook changes
  useEffect(() => {
    setCurrentNodeId("start");
    setHistory([]);
    hasTrackedCompletion.current = false;
  }, [playbook?.id]);

  const isLoading = playbookLoading || nodesLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Playbook Not Found</h1>
          <p className="text-muted-foreground mb-4">This playbook doesn't exist or is not available.</p>
          <Button onClick={() => window.location.href = "/"}>
            Go to HostCaptain
          </Button>
        </div>
      </div>
    );
  }

  if (!nodes || nodes.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">{playbook.title}</h1>
          <p className="text-muted-foreground">This playbook has no content yet.</p>
        </div>
      </div>
    );
  }

  // Build node map
  const nodeMap: Record<string, PlaybookNode> = {};
  nodes.forEach(node => {
    nodeMap[node.node_id] = node;
  });

  const currentNode = nodeMap[currentNodeId];

  if (!currentNode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Error</h1>
          <p className="text-muted-foreground">Node not found. The playbook may be incomplete.</p>
        </div>
      </div>
    );
  }

  const handleOptionClick = (option: typeof currentNode.options[0]) => {
    if (option.action) {
      if (option.action === "call_turo" || option.action === "call") {
        window.open("tel:4159654525");
      } else {
        navigator.clipboard.writeText(option.action);
        toast.success("Copied to clipboard");
      }
    }
    if (option.nextNode) {
      const nextNode = nodeMap[option.nextNode];
      // Track completion when reaching an end node
      if (nextNode?.is_end && !hasTrackedCompletion.current) {
        hasTrackedCompletion.current = true;
        analytics.track("Shared Playbook Completed", {
          playbook_name: playbook?.title,
          playbook_slug: slug,
          end_type: nextNode.end_type,
          steps_taken: history.length + 2, // current + next
        });
      }
      setHistory([...history, currentNodeId]);
      setCurrentNodeId(option.nextNode);
    }
  };

  const handleCtaClick = (location: "header" | "footer") => {
    analytics.track("Shared Playbook CTA Clicked", {
      playbook_name: playbook?.title,
      playbook_slug: slug,
      cta_location: location,
    });
    window.location.href = "/auth";
  };

  const handleBack = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousNode = newHistory.pop()!;
      setHistory(newHistory);
      setCurrentNodeId(previousNode);
    }
  };

  const handleRestart = () => {
    setCurrentNodeId("start");
    setHistory([]);
  };

  const getEndIcon = () => {
    switch (currentNode.end_type) {
      case "success":
        return <CheckCircle2 className="h-12 w-12 text-success" />;
      case "warning":
        return <AlertTriangle className="h-12 w-12 text-warning" />;
      case "action":
        return <MessageSquare className="h-12 w-12 text-info" />;
      default:
        return null;
    }
  };

  const Icon = getIcon(playbook.icon_name);

  return (
    <>
      <Helmet>
        <title>{playbook.title} | HostCaptain</title>
        <meta name="description" content={playbook.description} />
        <meta property="og:title" content={`${playbook.title} | HostCaptain`} />
        <meta property="og:description" content={playbook.description} />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card/50">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <Anchor className="h-6 w-6 text-primary" />
              <span className="font-semibold text-foreground">HostCaptain</span>
            </a>
            <Button variant="outline" size="sm" onClick={() => handleCtaClick("header")}>
              Sign Up Free
            </Button>
          </div>
        </header>

        <div className="p-8 max-w-3xl mx-auto">
          {/* Playbook Header */}
          <div className="mb-8 text-center">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{playbook.title}</h1>
            <p className="text-muted-foreground mt-1">{playbook.description}</p>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Badge variant="secondary">
              Step {history.length + 1}
            </Badge>
            {history.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleBack}>
                Back
              </Button>
            )}
            {history.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleRestart}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Start Over
              </Button>
            )}
          </div>

          {/* Decision Card */}
          <Card className={`${currentNode.is_end ? "border-2" : ""} ${
            currentNode.end_type === "success" ? "border-success" : 
            currentNode.end_type === "warning" ? "border-warning" : 
            currentNode.end_type === "action" ? "border-info" : ""
          }`}>
            <CardHeader className="text-center">
              {currentNode.is_end && (
                <div className="flex justify-center mb-4">
                  {getEndIcon()}
                </div>
              )}
              <CardTitle className="text-xl">{currentNode.question}</CardTitle>
              {currentNode.description && (
                <p className="text-muted-foreground mt-2">{currentNode.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentNode.options.map((option, index) => (
                  <Button
                    key={index}
                    variant={option.variant === "destructive" ? "destructive" : "outline"}
                    className="w-full justify-between text-left h-auto py-4 px-4"
                    onClick={() => handleOptionClick(option)}
                  >
                    <span>{option.label}</span>
                    {option.nextNode && <ArrowRight className="h-4 w-4 flex-shrink-0" />}
                    {option.action && !option.nextNode && (
                      <FileText className="h-4 w-4 flex-shrink-0" />
                    )}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          {currentNode.tip_text && (
            <Card className="mt-6 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Pro tip:</strong> {currentNode.tip_text}
                </p>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <Card className="mt-8 bg-primary/5 border-primary/20">
            <CardContent className="p-6 text-center">
              <h3 className="font-semibold text-foreground mb-2">Want more playbooks like this?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Sign up for HostCaptain to access all playbooks, track your fleet, and manage expenses.
              </p>
              <Button onClick={() => handleCtaClick("footer")}>
                Get Started Free
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
